import {
  Card,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import React, {
  cloneElement,
  isValidElement,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import { CardContentInner, Labeled, Record, useTranslate } from "react-admin";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import {
  DriverApiFactory,
  DriverPasswordOutput,
} from "../../../common/data/service-proxies";

interface Props<T> {
  record?: T & Record;
  onClick?: (e: any) => void;
  showUsername?: boolean;
}

export const ViewDriverPasswordAction = <T extends {} = Record>({
  record,
  onClick,
  children,
  showUsername,
}: PropsWithChildren<Props<T>>) => {
  const translate = useTranslate();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [data, setData] = useState<DriverPasswordOutput>();
  const axios = useAxiosInstance();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    if (!axios) {
      return;
    }

    if (!record) {
      return;
    }

    setLoading(true);
    DriverApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppDriverGetpasswordDriverIdGet(Number(record.id))
      .then((result) => {
        setData(result.data);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [axios, record, dialogOpen]);

  const child = React.Children.only(children);
  const clickHandler = (e: MouseEvent) => {
    setDialogOpen(true);
    if (typeof onClick === "function") {
      onClick(e);
    }
  };

  return (
    <>
      {isValidElement(child)
        ? cloneElement(child, { onClick: clickHandler } as any)
        : null}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {translate("resources.admin-drivers.labels.viewPasswordTitle", {
            driver: record?.driverName,
          })}
        </DialogTitle>
        <Divider />
        <Card>
          <div className="simple-form form-create-edit-drawer">
            <CardContentInner>
              {showUsername ? (
                <Labeled
                  fullWidth
                  label={translate("resources.common.labels.email")}
                >
                  <TextField
                    value={data?.email ?? ""}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {loading && (
                            <CircularProgress
                              style={{ width: "32px", height: "32px" }}
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Labeled>
              ) : null}
              <Labeled
                fullWidth
                label={translate("resources.admin-drivers.labels.password")}
              >
                <TextField
                  value={data?.password ?? ""}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {loading && (
                          <CircularProgress
                            style={{ width: "32px", height: "32px" }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Labeled>
            </CardContentInner>
          </div>
        </Card>
      </Dialog>
    </>
  );
};
